import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FaBible,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaSearch,
  FaTimes,
  FaGraduationCap,
  FaTv,
  FaBookOpen,
  FaLaptopCode,
  FaCompass,
  FaChurch,
} from 'react-icons/fa';
import DesktopNavigation from '../Navigation/DesktopNavigation';
import MobileNavigation from '../Navigation/MobileNavigation';
import Footer from '../Footer/Footer';
import SEOHead from '../SEOHead';
import { fetchBooks, Book } from '../../services/adminApi';
import {
  BIBLE_STUDY_RESOURCES,
  RESOURCE_CATEGORIES,
  BibleResource,
  DEFAULT_RECOMMENDED_BOOKS,
} from '../../data/bibleStudyResourcesData';
import hqWallpaper from '../../assets/hq-wallpaper.webp';
import logoVidaNueva from '../../assets/logo-vidanueva.png';
import logoBlessedHope from '../../assets/logo-blessedhope.png';
import logoLakepointe from '../../assets/logo-lakepointe.png';
import logoEleven22 from '../../assets/logo-eleven22.png';

const BibleStudyResourcesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [books, setBooks] = useState<Book[]>(DEFAULT_RECOMMENDED_BOOKS);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBooks()
      .then((data) => {
        if (data && data.length > 0) {
          setBooks(data);
        }
      })
      .catch((err) => console.error('Failed to load recommended books:', err));
  }, []);

  const isSpanish = i18n.language?.startsWith('es');

  // Filter resources based on active category and search query
  const filteredResources = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return BIBLE_STUDY_RESOURCES.filter((resource: BibleResource) => {
      // Category filter
      if (selectedCategory !== 'all' && resource.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (query) {
        const nameMatch = resource.name.toLowerCase().includes(query);
        const descEnMatch = resource.descriptionEn.toLowerCase().includes(query);
        const descEsMatch = resource.descriptionEs.toLowerCase().includes(query);
        const tagMatch = resource.tag ? resource.tag.toLowerCase().includes(query) : false;
        return nameMatch || descEnMatch || descEsMatch || tagMatch;
      }

      return true;
    });
  }, [selectedCategory, searchQuery]);

  // Map category to an appropriate icon
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case 'bible-apps':
        return <FaBible className="w-4 h-4" />;
      case 'education':
        return <FaGraduationCap className="w-4 h-4" />;
      case 'ministry-media':
        return <FaTv className="w-4 h-4" />;
      case 'study-materials':
        return <FaBookOpen className="w-4 h-4" />;
      case 'youtube-ministries':
        return <FaCompass className="w-4 h-4" />;
      default:
        return <FaLaptopCode className="w-4 h-4" />;
    }
  };

  // Category badge colors
  const getCategoryColor = (categoryId: string) => {
    switch (categoryId) {
      case 'bible-apps':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
      case 'education':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/30';
      case 'ministry-media':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'study-materials':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      case 'youtube-ministries':
        return 'bg-rose-500/10 text-rose-400 border-rose-500/30';
      default:
        return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0d14] text-gray-100 font-sans selection:bg-[#05a5b7] selection:text-white flex flex-col">
      <SEOHead
        title={t('resourcesPage.metaTitle')}
        description={t('resourcesPage.metaDescription')}
      />
      <DesktopNavigation />
      <MobileNavigation />

      <main className="flex-grow pt-28 xl:pt-36">
        {/* Hero Section */}
        <div className="relative py-12 md:py-16 overflow-hidden">
          {/* Background Wallpaper */}
          <div
            className="absolute inset-0 z-0 opacity-15"
            style={{
              backgroundImage: `url(${hqWallpaper})`,
              backgroundSize: 'cover',
              backgroundAttachment: 'fixed',
              backgroundPosition: 'center',
            }}
          ></div>

          {/* Radial Gradient Glow */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0d14]/60 via-transparent to-[#0a0d14] z-10 pointer-events-none"></div>

          <div className="container mx-auto px-6 relative z-20 max-w-6xl">
            {/* Back to Home Link */}
            <div className="mb-6">
              <Link
                to="/"
                className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 text-sm font-semibold transition-colors group"
              >
                <FaArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1" />
                <span>{t('resourcesPage.backToHome')}</span>
              </Link>
            </div>

            {/* Badge & Title */}
            <div className="text-center max-w-3xl mx-auto mb-10">
              <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-wider mb-4">
                <FaBible className="w-3.5 h-3.5" />
                <span>{t('footer.resources.title')}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight uppercase font-oswald mb-4">
                {t('resourcesPage.title')}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                  {t('resourcesPage.titleHighlight')}
                </span>
              </h1>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                {t('resourcesPage.subtitle')}
              </p>
            </div>

            {/* Search Input Bar */}
            <div className="max-w-2xl mx-auto mb-10">
              <div className="relative flex items-center">
                <FaSearch className="absolute left-4 text-gray-400 w-4 h-4 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('resourcesPage.searchPlaceholder')}
                  className="w-full pl-11 pr-10 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all text-sm backdrop-blur-md shadow-lg"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3.5 text-gray-400 hover:text-white p-1"
                    aria-label="Clear search"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10">
              {RESOURCE_CATEGORIES.map((category) => {
                const isActive = selectedCategory === category.id;
                const label = t(category.labelKey, { defaultValue: category.fallbackLabel });
                const count =
                  category.id === 'all'
                    ? BIBLE_STUDY_RESOURCES.length
                    : BIBLE_STUDY_RESOURCES.filter((r) => r.category === category.id).length;

                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 border ${
                      isActive
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-transparent shadow-lg shadow-cyan-500/20 scale-105'
                        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:border-white/20 hover:text-white'
                    }`}
                  >
                    <span>{label}</span>
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-white/20 text-white' : 'bg-white/10 text-gray-400'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Directory Cards Section */}
        <div className="container mx-auto px-6 max-w-6xl">
          {/* Results Counter */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10">
            <p className="text-gray-400 text-sm">
              {t('resourcesPage.showingCount', { count: filteredResources.length })}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-xs text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                {t('resourcesPage.clearSearch')}
              </button>
            )}
          </div>

          {/* Cards Grid */}
          {filteredResources.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => {
                const description = isSpanish
                  ? resource.descriptionEs
                  : resource.descriptionEn;

                return (
                  <a
                    key={resource.id}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col justify-between p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-cyan-400/50 hover:bg-white/[0.08] transition-all duration-300 shadow-xl hover:shadow-cyan-500/10 hover:-translate-y-1 relative"
                  >
                    <div>
                      {/* Top Bar: Category / Tag */}
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${getCategoryColor(
                            resource.category
                          )}`}
                        >
                          {getCategoryIcon(resource.category)}
                          <span>{resource.tag || resource.category}</span>
                        </div>
                        <FaExternalLinkAlt className="w-3.5 h-3.5 text-gray-500 group-hover:text-cyan-400 transition-colors" />
                      </div>

                      {/* Resource Name */}
                      <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors tracking-wide">
                        {resource.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-300 text-sm leading-relaxed mb-6">
                        {description}
                      </p>
                    </div>

                    {/* Footer link action */}
                    <div className="pt-4 border-t border-white/5 flex items-center justify-between text-xs font-semibold text-cyan-400 group-hover:text-cyan-300">
                      <span>{t('resourcesPage.visitResource')}</span>
                      <span className="transform group-hover:translate-x-1 transition-transform">
                        →
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-20 px-6 rounded-2xl bg-white/[0.02] border border-white/10 max-w-xl mx-auto">
              <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4 text-gray-400">
                <FaSearch className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('resourcesPage.noResultsTitle')}
              </h3>
              <p className="text-gray-400 text-sm mb-6">
                {t('resourcesPage.noResultsDesc')}
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="px-6 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-sm transition-colors"
              >
                {t('resourcesPage.clearSearch')}
              </button>
            </div>
          )}

          {/* More Media Button */}
          <div className="text-center mt-12 sm:mt-16">
            <a
              href="https://christreignslab.com/favorite-media"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-sm tracking-wide transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-500/25 border border-cyan-400/30 group"
            >
              <span>{t('resourcesPage.moreMedia', { defaultValue: 'More Media' })}</span>
              <FaExternalLinkAlt className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* Favorite Church Services Section */}
        <div className="container mx-auto px-6 max-w-6xl mt-20 pt-14 pb-20 border-t border-white/10">
          <div className="text-center md:text-left mb-8">
            <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-wider mb-3">
              <FaChurch className="w-3.5 h-3.5" />
              <span>{t("footer.favoriteChurchServices")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white uppercase tracking-wider font-oswald mb-2">
              {t("footer.favoriteChurchServices")}
            </h2>
            <p className="text-gray-400 text-sm">
              {t("resourcesPage.favoriteChurchServicesDesc", {
                defaultValue: "Recommended church livestreams, biblical preaching, and worship services to encourage your faith."
              })}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Centro Evangelico Vida Nueva */}
            <a
              href="https://youtu.be/uBayR05WIH0?si=GRjxQq9Q7uHHcH8Z"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-white/10 hover:border-cyan-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center pb-8">
                <img 
                  src={logoVidaNueva} 
                  alt="Centro Evangélico Vida Nueva Logo" 
                  className="w-14 h-14 object-contain rounded-full transition-transform duration-300 group-hover:scale-110 shadow-md"
                />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4 text-center">
                <h3 className="text-white text-sm font-bold tracking-wide group-hover:text-cyan-300 transition-colors line-clamp-1">
                  Centro Evangelico Vida Nueva
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  {t("footer.watchChannel")}
                </p>
              </div>
            </a>

            {/* Blessed Hope Chapel */}
            <a
              href="https://www.youtube.com/@BlessedHopeChapel"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-white/10 hover:border-cyan-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center pb-8">
                <img 
                  src={logoBlessedHope} 
                  alt="Blessed Hope Chapel Logo" 
                  className="w-14 h-14 object-contain rounded-full transition-transform duration-300 group-hover:scale-110 shadow-md"
                />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4 text-center">
                <h3 className="text-white text-sm font-bold tracking-wide group-hover:text-cyan-300 transition-colors line-clamp-1">
                  Blessed Hope Chapel
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  {t("footer.watchChannel")}
                </p>
              </div>
            </a>

            {/* Lakepointe Church */}
            <a
              href="https://www.youtube.com/@LakepointeChurch"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-white/10 hover:border-cyan-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center pb-8">
                <img 
                  src={logoLakepointe} 
                  alt="Lakepointe Church Logo" 
                  className="w-14 h-14 object-contain rounded-full transition-transform duration-300 group-hover:scale-110 shadow-md"
                />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4 text-center">
                <h3 className="text-white text-sm font-bold tracking-wide group-hover:text-cyan-300 transition-colors line-clamp-1">
                  Lakepointe Church
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  {t("footer.watchChannel")}
                </p>
              </div>
            </a>

            {/* The Church of Eleven22 */}
            <a
              href="https://www.youtube.com/@TheChurchofEleven22"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block aspect-video rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-white/10 hover:border-cyan-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center pb-8">
                <img 
                  src={logoEleven22} 
                  alt="The Church of Eleven22 Logo" 
                  className="w-14 h-14 object-contain rounded-full transition-transform duration-300 group-hover:scale-110 shadow-md"
                />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4 text-center">
                <h3 className="text-white text-sm font-bold tracking-wide group-hover:text-cyan-300 transition-colors line-clamp-1">
                  The Church of Eleven22
                </h3>
                <p className="text-gray-400 text-xs mt-0.5">
                  {t("footer.watchChannel")}
                </p>
              </div>
            </a>
          </div>
        </div>

        {/* Recommended Apologetics Reading Section */}
        {books && books.length > 0 && (
          <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 font-sans tracking-tight">
                {t('home.apologeticsTitle')}
              </h2>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-4 font-light">
                {t('home.apologeticsDesc')}
              </p>
              <div className="w-12 h-1 bg-[#38bdf8] mx-auto rounded mb-12" />
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6 justify-items-center mb-12">
                {books.map((book) => {
                  const bookCover = book.imageUrl ? (
                    <img
                      src={book.imageUrl}
                      alt={t(book.title)}
                      className="w-[180px] h-[260px] object-cover rounded-r-lg shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 border-l border-gray-200"
                    />
                  ) : (
                    <div className="w-[180px] h-[260px] rounded-r-lg shadow-md relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-br from-slate-800 to-slate-900 border-l-4 border-l-[#38bdf8]/70 flex flex-col justify-between p-4 text-left">
                      {/* Spine indent detail */}
                      <div className="absolute top-0 left-0 w-2 h-full bg-black/20" />
                      
                      <div className="flex flex-col gap-2">
                        <span className="text-[9px] uppercase font-bold text-[#38bdf8] tracking-wider font-sans">
                          Apologetics
                        </span>
                        <h4 className="text-xs font-bold text-white leading-snug line-clamp-5 uppercase tracking-wide">
                          {t(book.title)}
                        </h4>
                      </div>
                      
                      <div className="flex flex-col gap-0.5 border-t border-white/10 pt-2">
                        <span className="text-[8px] uppercase font-semibold text-gray-400">
                          Author
                        </span>
                        <span className="text-xs font-semibold text-white/90 line-clamp-1">
                          {book.author}
                        </span>
                      </div>
                    </div>
                  );

                  return (
                    <div key={book._id} className="flex flex-col items-center gap-3 w-[180px]">
                      {book.buyUrl ? (
                        <a href={book.buyUrl} target="_blank" rel="noopener noreferrer" className="block w-full">
                          {bookCover}
                        </a>
                      ) : (
                        bookCover
                      )}
                      <div className="text-center w-full mt-1">
                        <h5 className="text-xs font-bold text-gray-800 line-clamp-1 px-1 uppercase tracking-wide">
                          {t(book.title)}
                        </h5>
                        <p className="text-[10px] text-gray-500 font-medium line-clamp-1">
                          {book.author}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <a
                href="https://www.christreignsmedia.io/book-library"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#38bdf8] hover:bg-[#0ea5e9] text-white px-8 py-3 rounded-lg font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                {t('home.apologeticsMore')}
              </a>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BibleStudyResourcesPage;
